// LE-00 (promo): 行動ログ → 業態別「興味関心タグ」の派生（純関数）。
// admin（tenant-promo）/ functions（promoTrackEvent）の双方から参照するため
// Web SDK / Node SDK 依存は持たない（fuelPriceDerive.ts と同じ方針）。
//
// 設計:
//   - 興味タグは 3 段階（estimated < self_declared < confirmed）の単調昇格。
//     行動が増えるほど確度が上がる方向にのみ更新し、downgrade しない（レース安全）。
//   - estimated は score（low < mid < high）の強度を持つ。MVP は 1 タップで mid。
//     high（7 日 2 回ルール）はバッチ再計算の後フェーズ送り。
//   - メニュー / カテゴリ → 興味キーのマッピングは本ファイルを単一の真実源とする。
//
// M-62: タグ付け条件（メニュー/カテゴリ対応・行動種別→確度）と表示名を `InterestTagConfig`
//   （テナント別設定）で上書きできる。各派生関数は config を **引数 DI** で受け取り、
//   省略時は既定マッピング（DEFAULT_*）にフォールバックする（後方互換 / SDK 非依存を維持）。
import { SURVEY_CHOICE_KEYS } from './types.js';
/**
 * メニュー ID → 興味キーの既定マッピング（本番 seed `booking_seed_v2.json` 準拠）。
 * ここに無いメニュー（hand_wash / oil_change / tire_change 等の汎用整備）は
 * 業態の興味シグナルにしない。テナント設定（InterestTagConfig.tags）で上書き可能。
 */
export const MENU_TO_INTEREST = {
    keeper_crystal: 'keeper',
    keeper_fresh: 'keeper',
    keeper_eco_diamond: 'keeper',
    keeper_eco_diamond_premium: 'keeper',
    visit_washpass: 'washpass',
    visit_autoflat_quote: 'car_sales',
    visit_car_appraisal: 'car_sales',
    visit_used_car_search: 'car_sales',
    rakuraku_pre_inspection: 'inspection',
};
/**
 * カテゴリ ID → 興味キーの既定マッピング（メニュー ID が無い粗い分類のフォールバック）。
 * wash カテゴリは KeePer と汎用洗車が混在し確定できないため意図的にマップしない。
 */
export const CAT_TO_INTEREST = {
    inspection: 'inspection',
};
/** 興味キー → 既定表示ラベル（日本語）。テナント設定で上書き可能（resolveInterestLabel）。 */
export const DEFAULT_INTEREST_LABELS = {
    car_sales: '車販売・買取',
    inspection: '車検',
    keeper: 'KeePer コーティング',
    washpass: 'WashPass（洗車サブスク）',
    fuel: '給油（燃料）',
};
/**
 * 行動種別 → 付与する興味タグの既定確度（`null` は「興味タグを動かさない」）。
 * - reservation_completed → confirmed（最強シグナル）
 * - coupon_redeem → confirmed（M-73: クーポン店頭使用 = 来店の事実。現行の記録 payload は
 *   couponId のみで商材に解決できないため実発火は M-80 のクーポン⇄商材紐づけ待ち）
 * - liff_link_tap / richmenu_tap → estimated:mid
 * - liff_visit → estimated:low（M-70: I 入りの最弱シグナル。mid から引き下げ）
 * - coupon_claim → estimated:mid（M-70: 欲求のシグナルだが行動からの推定のため
 *   他の推定イベントと同水準。ステージ軸では I→D 昇格イベント = DEFAULT_STAGE_PROMOTION_RULES）
 * - booking_page_reach → estimated:high（M-73: 予約直前離脱 = 強い欲求。推定の最上位）
 * - survey_answer → self_declared（M-73 で先行定義: アンケート回答 = 自己申告。
 *   記録経路とステージ配線は M-78 の設問設計と同期）
 * - message_sent → null（配信記録 / ユーザー行動ではない）
 * - cycle_reset → confirmed（M-75: 期限/周期の到来は事実。§3.2 の属性トリガーを
 *   日次バッチの機械イベントとして実現 — D×confirmed の自動再セット）
 * - hot_won → confirmed（M-76: 担当者が記録した成約は事実。§7「クローズ: 成約=Act」。
 *   ステージ軸では toAction = DEFAULT_STAGE_PROMOTION_RULES）
 * - follow → null（M-87: 友だち追加は「配信母集団への編入」だけが目的で、どの商材への
 *   関心も示さない。`resolveInterestKeyFromEvent` も payload 無しでは商材を解決できないため
 *   構造的にも寄与不能。テナント上書きの対象外 = UI の EDITABLE_EVENT_TYPES 非掲載）
 * - stage_changed → null（M-142: ステージ遷移の通知イベント。再集計の**出力**なので
 *   入力（確度・ステージ）に算入すると自己参照になる。昇格ルールにも載せない）
 */
export const DEFAULT_EVENT_LEVELS = {
    reservation_completed: { level: 'confirmed' },
    liff_link_tap: { level: 'estimated', score: 'mid' },
    richmenu_tap: { level: 'estimated', score: 'mid' },
    liff_visit: { level: 'estimated', score: 'low' },
    message_sent: null,
    coupon_claim: { level: 'estimated', score: 'mid' },
    coupon_redeem: { level: 'confirmed' },
    survey_answer: { level: 'self_declared' },
    booking_page_reach: { level: 'estimated', score: 'high' },
    cycle_reset: { level: 'confirmed' },
    hot_won: { level: 'confirmed' },
    follow: null,
    stage_changed: null,
    // M-164: 抽選結果。来店（給油）の事実は券の配布時点で確定しているが商材キーを持たないため null。
    lottery_draw: null,
};
/** 確度レベルの序列（大きいほど強い）。テナント設定の対象外（固定）。 */
const LEVEL_RANK = {
    estimated: 0,
    self_declared: 1,
    confirmed: 2,
};
/** estimated の score 序列（大きいほど強い）。テナント設定の対象外（固定）。 */
const SCORE_RANK = {
    low: 1,
    mid: 2,
    high: 3,
};
/**
 * 興味タグの「強さ」を数値化する。
 * level を主、estimated 内の score を従として比較する（confirmed > self_declared > estimated:high > estimated:mid > estimated:low）。
 */
export function interestStrength(tag) {
    const base = LEVEL_RANK[tag.level] * 10;
    if (tag.level === 'estimated') {
        const s = tag.score ? SCORE_RANK[tag.score] : SCORE_RANK.low;
        return base + s;
    }
    return base;
}
/**
 * config から「メニュー ID → 興味キー」「カテゴリ ID → 興味キー」の逆引きマップを構築する。
 * `config.tags` が未定義なら null を返し、呼び出し側は既定マッピング（MENU_TO_INTEREST 等）に
 * フォールバックする。`config.tags` が定義済みなら **config が真実源**（未設定キーは対応なし）。
 *
 * **M-103: 引ける対応が 1 件も無いときも null を返す。**
 * 呼び出し側は `lookup?.menu ?? MENU_TO_INTEREST` で既定へ倒すが、**空オブジェクト `{}` は
 * nullish ではないので `??` を素通りする**。この 1 行が機序の中心で、`config.tags` が定義済み
 * かつ中身が空だと「マッピングが 1 件も無い状態」が真実源として採用され、テナント全 users の
 * 興味タグが消えたうえ **UI から既定へ戻す経路が無くなる**（保存は成功するので気付けない）。
 *
 * 到達経路は 2 つあり、**書込側の是正だけでは片方しか塞がらない**:
 * - `{ tags: { car_sales: {}, inspection: {}, … } }` = 全キーが空（→ 書込側で空 override を捨てる）
 * - **ラベルだけ残してメニュー・カテゴリのチェックを全部外す**（`label` があると override が
 *   非空になるため書込側では落ちない / UI はラベル欄に既定ラベルをプリロードするので自然な操作）
 *
 * 「全業態を明示的に『対応なし』にする」運用は不要と確認済み（2026-08-13 / ユーザー判断）。
 * その運用が将来必要になったら、空マップと「明示的に対応なし」を doc 上で区別できる形
 * （明示フラグ等）が要る — `{}` の有無では区別できない。
 */
function buildLookup(config) {
    if (!config?.tags)
        return null;
    const menu = {};
    const cat = {};
    for (const [k, override] of Object.entries(config.tags)) {
        if (!override)
            continue;
        for (const m of override.menuIds ?? [])
            menu[m] = k;
        for (const c of override.categoryIds ?? [])
            cat[c] = k;
    }
    // M-103: 引ける対応が 1 件も無いなら「未設定」と同じ扱いにして既定へ倒す（上の JSDoc 参照）。
    if (Object.keys(menu).length === 0 && Object.keys(cat).length === 0)
        return null;
    return { menu, cat };
}
/**
 * 1 イベントから興味キーを解決する。
 * 優先順: payload.interestKey（server 専用の直接指定 / M-75）→ payload.targetMenuId →
 * payload.menuId → payload.targetCat → payload.categoryId。
 * いずれも業態にマップできなければ null（興味タグを動かさない）。
 * config 指定時は config のメニュー/カテゴリ対応を、未指定時は既定マッピングを使う。
 */
export function resolveInterestKeyFromEvent(event, config) {
    const p = event.payload;
    if (!p)
        return null;
    // M-75: cycle_reset 等の server 書込はメニュー/カテゴリを介さず商材キーを直接持つ。
    // Firestore 由来の値のため既知キーのみ通す（不正値はマッピング解決へフォールバック）。
    if (p.interestKey !== undefined && p.interestKey in DEFAULT_INTEREST_LABELS) {
        return p.interestKey;
    }
    const lookup = buildLookup(config);
    const menuMap = lookup?.menu ?? MENU_TO_INTEREST;
    const catMap = lookup?.cat ?? CAT_TO_INTEREST;
    const menuId = p.targetMenuId ?? p.menuId;
    if (menuId && menuMap[menuId]) {
        return menuMap[menuId];
    }
    const cat = p.targetCat ?? p.categoryId;
    if (cat && catMap[cat]) {
        return catMap[cat];
    }
    return null;
}
/**
 * イベント種別が示す興味タグ（強さ）を返す。`null` は興味タグを動かさない種別。
 * config.eventLevels に当該種別の設定（`null` 含む）があればそれ、無ければ既定（DEFAULT_EVENT_LEVELS）。
 */
export function interestTagForEvent(eventType, now, config) {
    const override = config?.eventLevels?.[eventType];
    const src = override !== undefined ? override : (DEFAULT_EVENT_LEVELS[eventType] ?? null);
    if (!src)
        return null;
    if (src.level === 'estimated') {
        return { level: 'estimated', score: src.score ?? 'mid', updatedAt: now };
    }
    return { level: src.level, updatedAt: now };
}
/**
 * 1 イベントを現在の興味タグ集合に適用し、**新しい集合を返す**（非破壊 / 単調昇格）。
 * `promoTrackEvent` の transaction 内で増分更新するために使う。
 */
export function applyEventToInterest(current, event, now, config) {
    const base = { ...(current ?? {}) };
    const key = resolveInterestKeyFromEvent(event, config);
    if (!key)
        return base;
    let candidate = interestTagForEvent(event.eventType, now, config);
    if (!candidate)
        return base;
    // M-79: **顧客の自己申告に由来する周期リセットは confirmed へ昇格させない**。
    // cycle_reset は既定 confirmed（「期限の到来は事実」）だが、その期限が
    // `inspectionDueSource: 'self_declared'`（アンケートで顧客が選んだ年月）由来の場合、
    // 事実として確定しているのは「顧客がそう申告した」ことだけで、車検証で確認した
    // confirmed と同じ確度に置くと `minLevel: 'confirmed'` で絞った配信
    // （＝車検が確定している客だけに送る）に未確認の申告客が混ざる。
    // 確度は単調昇格なので、一度 confirmed に上げると申告が誤りでも二度と下がらない。
    if (event.eventType === 'cycle_reset' &&
        event.payload?.dueSource === 'self_declared' &&
        candidate.level === 'confirmed') {
        candidate = { level: 'self_declared', updatedAt: now };
    }
    const existing = base[key];
    // 単調: 既存が同等以上なら更新しない（弱化させない）。
    if (existing && interestStrength(existing) >= interestStrength(candidate)) {
        return base;
    }
    return { ...base, [key]: candidate };
}
/**
 * 複数イベントから興味タグ集合をバッチ再計算する（検証 / 再集計用）。
 * `applyEventToInterest` の fold。単調昇格なので順序に依存しない結果になる。
 * M-62: 設定変更時の全件再集計（promoRecomputeInterestTags）で config を渡して呼ぶ。
 * （注: estimated:high への昇格＝7 日 2 回ルールは後フェーズ。本関数は mid 止まり。）
 */
export function deriveInterestTags(events, now, config) {
    return events.reduce((acc, ev) => applyEventToInterest(acc, ev, now, config), {});
}
/**
 * 興味キーの表示ラベルを解決する。config に表示名の上書き（空でない）があればそれ、
 * 無ければ既定ラベル（DEFAULT_INTEREST_LABELS）。UI 表示の単一の真実源。
 */
export function resolveInterestLabel(key, config) {
    const custom = config?.tags?.[key]?.label?.trim();
    return custom && custom.length > 0 ? custom : DEFAULT_INTEREST_LABELS[key];
}
/**
 * 既定マッピング（MENU_TO_INTEREST / CAT_TO_INTEREST / DEFAULT_INTEREST_LABELS）を
 * `InterestTagConfig.tags` 形式に変換する（設定モーダルの初期値プリロード用）。
 * 既存 config が無いテナントで「現状の挙動」をフォームに表示し、保存で config に焼き込むために使う。
 */
export function defaultInterestTagOverrides() {
    const tags = {};
    const ensure = (k) => (tags[k] ??= {});
    for (const k of Object.keys(DEFAULT_INTEREST_LABELS)) {
        ensure(k).label = DEFAULT_INTEREST_LABELS[k];
    }
    for (const [menuId, k] of Object.entries(MENU_TO_INTEREST)) {
        const o = ensure(k);
        o.menuIds = [...(o.menuIds ?? []), menuId];
    }
    for (const [catId, k] of Object.entries(CAT_TO_INTEREST)) {
        const o = ensure(k);
        o.categoryIds = [...(o.categoryIds ?? []), catId];
    }
    return tags;
}
/** 既定の行動種別 → 確度設定を返す（設定モーダルの初期値プリロード用 / 非破壊コピー）。 */
export function defaultInterestEventLevels() {
    const out = {};
    for (const [type, level] of Object.entries(DEFAULT_EVENT_LEVELS)) {
        out[type] = level ? { ...level } : null;
    }
    return out;
}
// ---- M-69: ステージ軸（AIDA 4段階 / docs/INTEREST-ENGINE-DESIGN.md §2・§4） ----
// 確度軸（LEVEL_RANK / interestStrength）とは直交する別軸。eventLevels 設定には
// 依存せず、イベント種別と商材キー解決（resolveInterestKeyFromEvent）のみで判定する。
/** ステージの昇格順（インデックスが序列値）。 */
export const INTEREST_STAGES = [
    'attention',
    'interest',
    'desire',
    'action',
];
/** ステージ序列（大きいほど購買に近い）。テナント設定の対象外（固定）。 */
const STAGE_RANK = {
    attention: 0,
    interest: 1,
    desire: 2,
    action: 3,
};
/** ステージの序列値を返す（`interestStrength` のステージ軸版）。 */
export function stageRank(stage) {
    return STAGE_RANK[stage];
}
/**
 * ステージ昇格ルールの既定値（§4.1）。
 * - toInterest: 能動接触 1 回で I（message_sent は配信記録のため含めない）。
 *   booking_page_reach も接触に数える（M-73）: 予約ページ到達は商材への能動接触そのもので
 *   あり、かつ M-72 減衰の鮮度アンカー（lastTouchAt）は toInterest 集合のみが立てるため、
 *   含めないと booking_page_reach 単発の desire が判定材料なし = fail-closed で即減衰し
 *   空振りする。**`coupon_claim` は本集合には入れないまま `isCouponClaimTouch`（M-84）で
 *   `lastTouchAt` にのみ算入する** — 累積 I→D の接触に数えると単発 D 直行と二重取りになるため
 * - toDesire: coupon_claim 単発で D（M-70 / §4.1 I→D 条件(b)。発火には商材紐づき
 *   payload が必要 = 現行の claim 記録は couponId のみで未発火）+ booking_page_reach
 *   （M-73 / 条件(d)）。survey_answer は回答内容（payload.surveyChoice）で到達ステージが
 *   分岐するため**静的集合には入れない** — `stageGrantedByEvent` 先頭の payload 条件分岐
 *   （`stageGrantedBySurveyChoice`）で処理する（M-73 仕上げ / §3.2・§6。toDesire に入れると
 *   「関心なし」でも D、toInterest に入れると not_interested が累積 I→D に算入される事故）
 * - toDesireCumulative: 鮮度窓×累積接触の I→D（M-71 / §4.1 条件(a)）。値は §5 の
 *   商材別既定値（仮置き / TP-12 パイロットで検証）。燃料は昇格対象外（タイプ③）
 * - toAction: 事実イベントのみ（coupon_redeem の確度側は M-73 で confirmed 配線済み。
 *   商材紐づけ = 実発火は M-80）。M-76 で `hot_won`（HOT 案件の成約）を追加 —
 *   タイプ②の成約は予約完了・クーポン利用として現れないため、担当者が記録した
 *   成約を Act として算入する唯一の経路（`promoHotLeadSetStatus` が payload.interestKey
 *   付きで発行するため `resolveInterestKeyFromEvent` が商材を解決できる）
 * - cycle_reset は toDesire のみ（M-75: 次周期 D の自動セット）。toInterest には
 *   **含めない** — 接触（lastTouchAt）ではない機械イベントのため、鮮度アンカーは
 *   `cycleDueAt`（期限＋14日保持）が担う
 * - follow（M-87）はどの集合にも**含めない** — 友だち追加は商材に紐づかず（payload なし =
 *   `resolveInterestKeyFromEvent` が null）、AIDA の A は「キー欠落 = attention」の既定と
 *   同義なのでステージを動かす余地が無い。母集団編入（users doc 作成）だけが目的
 */
export const DEFAULT_STAGE_PROMOTION_RULES = {
    toInterest: ['richmenu_tap', 'liff_link_tap', 'liff_visit', 'booking_page_reach'],
    toDesire: ['coupon_claim', 'booking_page_reach', 'cycle_reset'],
    toAction: ['reservation_completed', 'coupon_redeem', 'hot_won'],
    toDesireCumulative: {
        inspection: { windowDays: 21, touchCount: 2 },
        car_sales: { windowDays: 30, touchCount: 2 },
        keeper: { windowDays: 14, touchCount: 2 },
        washpass: { windowDays: 14, touchCount: 2 },
        fuel: null,
    },
};
/**
 * survey_answer の回答値 → 到達ステージ（M-73 仕上げ / §3.2・§6 の入り方）。
 * - `slightly_interested`（少し気になる）→ interest
 * - `interested_undecided` / `interested_planned`（関心あり）→ desire（時期未定/具体は
 *   確度・ステージ上は同値 — 区別は M-76 の HOT 判定「時期の具体化」= 回答変化が消費する）
 * - `not_interested`（関心なし）・欠落・未知値 → attention（fail-closed / ステージを動かさない。
 *   「関心なし」の実効降格はパーティション、配信抑制は `lastSurveyAnswers` 経由で評価時に行う）
 */
export function stageGrantedBySurveyChoice(choice) {
    switch (choice) {
        case 'slightly_interested':
            return 'interest';
        case 'interested_undecided':
        case 'interested_planned':
            return 'desire';
        default:
            return 'attention';
    }
}
/**
 * survey_answer がステージ付与に至る回答（slightly_interested / interested_*）なら true。
 * **lastTouchAt アンカー（M-72 減衰）にのみ**数える — booking_page_reach と同じ「単発 D が
 * 判定材料なし = fail-closed で即減衰し空振りする」対策。累積 I→D の touchDays には
 * **数えない**（§4.1(a) の能動接触ではなく、回答自体が直接 I/D を付与するため二重取りになる）。
 * 増分経路（trackEvent の touchedInterestKey）と再集計で同一の接触定義を共有する。
 */
export function isSurveyTouch(event) {
    return (event.eventType === 'survey_answer' &&
        stageRank(stageGrantedBySurveyChoice(event.payload?.surveyChoice)) >=
            stageRank('interest'));
}
/**
 * `coupon_claim` が **desire 付与に至る** claim なら true（M-84 / `isSurveyTouch` と同型）。
 *
 * **lastTouchAt アンカー（M-72 減衰）にのみ**数える。M-80 で claim に商材紐づけが入り D 昇格が
 * 発火するようになったが、`coupon_claim` は `toInterest` に属さず、**有効期限のないクーポンは
 * `couponUsableUntil` アンカーにも寄らない**ため、claim で得た D が次の `applyStageDecay` で
 * 鮮度根拠なし = fail-closed で即 interest へ、さらに `touchWithin(interestDays)` も false で
 * attention + dormant まで落ちていた（= claim の効果が一過性で終わる）。
 *
 * 算入しない集合（**非対称は意図的**）:
 * - 累積 I→D の `touchDays`（§4.1(a)）— claim は単発 D 直行の経路なので二重取りになる
 *   （`isSurveyTouch` と同じ理由）
 * - HOT の行動急増判定（`promoHot.ts` の `evaluateActivitySurge`）— M-76 の
 *   `surgeEventCount` の意味が変わり起票が増える
 *
 * `stageGrantedByEvent` に委譲するため、**`couponClaimToDesire` gate（M-74）で D を塞いだ商材や
 * `toDesire` から claim を外した rules では false** になる（gate の意図 =「この商材では claim を
 * D の根拠にしない」を鮮度アンカー側でも尊重する）。
 */
export function isCouponClaimTouch(event, key, rules = DEFAULT_STAGE_PROMOTION_RULES) {
    return (event.eventType === 'coupon_claim' &&
        stageRank(stageGrantedByEvent(event, key, rules)) >= stageRank('desire'));
}
/** イベントが単発で与える到達ステージ（最上位のルールを優先 / survey は payload 分岐）。 */
export function stageGrantedByEvent(event, key, rules) {
    const type = event.eventType;
    // M-73 仕上げ: survey_answer は回答内容で I/D/A（付与なし）が分岐する（静的集合 不参加）。
    if (type === 'survey_answer') {
        return stageGrantedBySurveyChoice(event.payload?.surveyChoice);
    }
    if (rules.toAction.includes(type))
        return 'action';
    if (rules.toDesire.includes(type)) {
        // M-74: coupon_claim 単発 I→D の商材別ゲート（§5「クーポン取得でD直行」列 /
        // gate は desire 付与のみを塞ぎ、toInterest 所属時の interest 付与は妨げない。
        // キー省略・map 省略 = 許可で後方互換）。
        if (type === 'coupon_claim' && rules.couponClaimToDesire?.[key] === false) {
            return rules.toInterest.includes(type) ? 'interest' : 'attention';
        }
        return 'desire';
    }
    if (rules.toInterest.includes(type))
        return 'interest';
    return 'attention';
}
const MS_PER_DAY = 86_400_000;
const JST_OFFSET_MS = 9 * 3_600_000;
/**
 * epoch ms → JST 暦日の通し番号（M-71 / TZ=Asia/Tokyo 固定規約）。
 * 接触日の同異・日差の計算に使う。JST は夏時間がないため固定オフセットで足る。
 * M-75 で export 化（promoChurn.ts の来店日集合・promoCycle.ts の窓判定が同一規約を共有）。
 */
export function jstDayNumber(epochMs) {
    return Math.floor((epochMs + JST_OFFSET_MS) / MS_PER_DAY);
}
/**
 * 接触日集合（JST 暦日番号）の中に「日差 windowDays 以内に touchCount 日」の並びが
 * あるか（スライディング窓 / 両端の日を含む）。同一暦日は集合化で 1 接触に潰れている
 * 前提。touchCount < 1 は設定不正として発火させない（fail-closed）。
 */
function hasCumulativeTouchRun(touchDayNumbers, rule) {
    if (rule.touchCount < 1 || touchDayNumbers.size < rule.touchCount)
        return false;
    const days = [...touchDayNumbers].sort((a, b) => a - b);
    const span = rule.touchCount - 1;
    for (let i = 0; i + span < days.length; i++) {
        const first = days[i];
        const last = days[i + span];
        if (first !== undefined && last !== undefined && last - first <= rule.windowDays) {
            return true;
        }
    }
    return false;
}
/**
 * 複数イベントから商材ごとの到達最高ステージ＋減衰評価メタをバッチ算出する
 * （§4.3 ①〜③ / M-72 で `deriveInterestStages` の詳細版として分離）。
 * - `evaluatedAt` より後（または時刻不明）のイベントは無視する（評価基準時刻までで判定。
 *   再集計・スナップショット再現用）。`evaluatedAt` 自体が parse 不能な場合は throw
 *   （黙って基準時刻フィルタが無効になる fail-open を防ぐ）
 * - 接触なし（attention）のキーは原則結果に含めない — 欠落 = attention と読む。ただし
 *   **事実アンカー（lastTouchAt / couponUsableUntil / lastActionAt / cycleDueAt）を持つキーは
 *   stage='attention' でも含める**（M-73 仕上げ: 「関心なし」パーティションで実効 A に降格しても
 *   M-75 周期リマインダーの起点 = lastActionAt / cycleDueAt を失わない）。attention エントリは
 *   stage 射影（deriveInterestStages）・再集計の interestStages 保存からは除外される
 * - 単発昇格イベントは下位ステージの通過を前提としない（イベント自体が下位接触を
 *   含意するため。到達最高ステージ = 発火したルールの最大値）
 * - M-71: I→D 条件(a) = `rules.toDesireCumulative` の鮮度窓×累積接触。接触に数える
 *   のは `rules.toInterest` の能動接触（同一 JST 暦日は 1 接触 / 窓はスライディング）
 * - M-72: `lastTouchAt` = 接触（同上の定義 + `isSurveyTouch` / **M-84 の
 *   `isCouponClaimTouch`**）の最新 createdAt。`couponUsableUntil` = coupon_claim の
 *   `payload.couponId` を `couponUsableUntilById` で解決した店頭利用期限の最大値
 *   （無期限クーポン・lookup 不能は寄与しない = だから M-84 が `lastTouchAt` 側で受ける）。
 *   減衰の適用は `applyStageDecay`
 * - M-75: **周期パーティション**（§4.3 AIDA+R の Act→D）。key ごとの最新 cycle_reset
 *   （resetAt）より前のイベントはステージ付与・累積接触に算入しない（過去の Act が
 *   永遠に action を占有しないように）。reset 自体は toDesire で desire を付与する。
 *   事実アンカー（lastTouchAt / couponUsableUntil / lastActionAt）はパーティション
 *   **しない**（実行動の記録は消えない）。`lastActionAt` = toAction イベントの最新
 *   createdAt（周期の「前回実施」）、`cycleDueAt` = cycle_reset payload.dueDate の
 *   最大値（`T00:00:00+09:00` 正規化 / 再セット D の減衰保持アンカー）
 */
export function deriveInterestStageDetails(events, evaluatedAt, config, rules = DEFAULT_STAGE_PROMOTION_RULES, couponUsableUntilById) {
    const cutoff = Date.parse(evaluatedAt);
    if (!Number.isFinite(cutoff)) {
        throw new RangeError(`deriveInterestStageDetails: invalid evaluatedAt: ${evaluatedAt}`);
    }
    // M-75: 1 パス目 — 周期パーティション起点（key ごとの最新 cycle_reset）と期限アンカー
    // （cycleDueAt = payload.dueDate の最大値）を確定する。2 パス構成なのは結果の
    // 順序非依存を保つため（1 パスだと reset より後に読んだ過去イベントを除外できない）。
    // M-73 仕上げ: 「関心なし」回答（survey_answer × not_interested）も別のパーティション起点
    // （niAtMs）として確定する — 宣言より前の接触・D・Act をステージ付与に算入しない
    // （= 実効 A へ降格 / §6「A×self_declared」マス）。宣言より後の行動・「関心あり」への
    // 答え直しは通常どおり再昇格。cycle_reset 起点（at < reset で除外 = reset 自身は通過して
    // desire を自己付与）と異なり、「関心なし」起点は**同秒の付与イベントも除外**（at <= ni）—
    // lastSurveyAnswers の同時刻タイブレーク（not_interested 優先 = 抑制側）と整合させ、
    // 「desire × not_interested」の恒常矛盾ペアを残さないため（レビュー修正）。
    // 事実アンカー（lastTouchAt / couponUsableUntil / lastActionAt）は従来どおり非パーティション。
    const resetAtMs = new Map();
    const niAtMs = new Map();
    const cycleDue = new Map();
    for (const event of events) {
        const isCycleReset = event.eventType === 'cycle_reset';
        const isNotInterested = event.eventType === 'survey_answer' &&
            event.payload?.surveyChoice === 'not_interested';
        if (!isCycleReset && !isNotInterested)
            continue;
        const at = Date.parse(event.createdAt);
        if (!Number.isFinite(at) || at > cutoff)
            continue;
        const key = resolveInterestKeyFromEvent(event, config);
        if (!key)
            continue;
        if (isNotInterested) {
            const prevNi = niAtMs.get(key);
            if (prevNi === undefined || at > prevNi)
                niAtMs.set(key, at);
            continue;
        }
        const prevReset = resetAtMs.get(key);
        if (prevReset === undefined || at > prevReset)
            resetAtMs.set(key, at);
        const dueDate = event.payload?.dueDate;
        if (dueDate) {
            const dueIso = `${dueDate}T00:00:00+09:00`;
            const dueMs = Date.parse(dueIso);
            if (Number.isFinite(dueMs)) {
                const prevDue = cycleDue.get(key);
                if (!prevDue || dueMs > prevDue.ms)
                    cycleDue.set(key, { ms: dueMs, iso: dueIso });
            }
        }
    }
    const stages = {};
    const touchDays = new Map();
    const lastTouch = new Map();
    const couponUntil = new Map();
    const lastAction = new Map();
    for (const event of events) {
        const at = Date.parse(event.createdAt);
        if (!Number.isFinite(at) || at > cutoff)
            continue;
        const key = resolveInterestKeyFromEvent(event, config);
        if (!key)
            continue;
        if (rules.toInterest.includes(event.eventType) ||
            isSurveyTouch(event) ||
            isCouponClaimTouch(event, key, rules)) {
            // 最終接触（M-72 減衰の事実アンカー）: パーティション対象外。
            // M-73 仕上げ: 付与に至る survey 回答も接触に数える（isSurveyTouch の JSDoc 参照）。
            // M-84: D 付与に至る coupon_claim も接触に数える（無期限クーポンで D が即減衰するのを防ぐ /
            // 累積 touchDays・HOT 急増判定には算入しない = isCouponClaimTouch の JSDoc 参照）。
            const prevTouch = lastTouch.get(key);
            if (!prevTouch || at > prevTouch.ms) {
                lastTouch.set(key, { ms: at, iso: event.createdAt });
            }
        }
        if (rules.toAction.includes(event.eventType)) {
            // M-75: 最終 Act（周期の「前回実施」）。事実の記録のためパーティション対象外。
            const prevAct = lastAction.get(key);
            if (!prevAct || at > prevAct.ms) {
                lastAction.set(key, { ms: at, iso: event.createdAt });
            }
        }
        if (event.eventType === 'coupon_claim') {
            const couponId = event.payload?.couponId;
            const untilIso = couponId ? couponUsableUntilById?.[couponId] : undefined;
            const untilMs = untilIso !== undefined ? Date.parse(untilIso) : NaN;
            if (untilIso !== undefined && Number.isFinite(untilMs)) {
                const prevUntil = couponUntil.get(key);
                if (!prevUntil || untilMs > prevUntil.ms) {
                    couponUntil.set(key, { ms: untilMs, iso: untilIso });
                }
            }
            // M-84 で解決済み（旧 M-80 申し送り）: **無期限クーポン**（usableUntil なし / lookup 不能）は
            // 上の couponUntil に寄らないため、claim 直後でも desire の鮮度根拠を持たず
            // applyStageDecay で即 fail-closed 減衰していた。対策は「claim 時刻 + 猶予日数」を
            // 期限アンカーに足す案ではなく（InterestStageMeta は事実のみを持ちルール値を焼かない
            // 設計 / types.ts の JSDoc 参照）、**claim 自体を lastTouchAt に算入する**方式を採った
            // （上の isCouponClaimTouch ブロック）。lastTouchAt は interestDays にも効くため、
            // D→I 降格後に即 attention まで落ちる穴も同時に閉じる。
        }
        // M-75: 周期パーティション — 最新 cycle_reset より前のイベントはステージ付与・
        // 累積接触に算入しない（reset 自体は at === resetAt のため通過し desire を付与）。
        const partitionMs = resetAtMs.get(key);
        if (partitionMs !== undefined && at < partitionMs)
            continue;
        // M-73 仕上げ: 「関心なし」起点は同秒も除外（<= / 1 パス目のコメント参照）。cycle_reset も
        // 対象（同秒なら機械イベントより自己申告の抑制を優先 / 実運用では 04:10 バッチと衝突しない）。
        const niMs = niAtMs.get(key);
        if (niMs !== undefined && at <= niMs)
            continue;
        if (rules.toInterest.includes(event.eventType)) {
            // 接触（M-71 の定義）: 累積 I→D 判定に使う。
            const days = touchDays.get(key) ?? new Set();
            days.add(jstDayNumber(at));
            touchDays.set(key, days);
        }
        const granted = stageGrantedByEvent(event, key, rules);
        if (granted === 'attention')
            continue;
        if (stageRank(granted) > stageRank(stages[key] ?? 'attention')) {
            stages[key] = granted;
        }
    }
    // M-71: 鮮度窓×累積接触の I→D（到達最高ステージが desire 未満のキーのみ昇格）。
    for (const [key, days] of touchDays) {
        const rule = rules.toDesireCumulative?.[key];
        if (!rule)
            continue;
        if (stageRank(stages[key] ?? 'attention') >= stageRank('desire'))
            continue;
        if (hasCumulativeTouchRun(days, rule)) {
            stages[key] = 'desire';
        }
    }
    const out = {};
    // M-73 仕上げレビュー修正: 出力は「ステージ到達キー ∪ 事実アンカー保持キー」。stages のキー
    // だけを出力すると、「関心なし」パーティションで実効 A に降格したキーの lastActionAt /
    // cycleDueAt（M-75 周期リマインダーの起点）/ lastTouchAt まで再集計で失われる
    // （cycle_reset 起点は自己 desire 付与のため stages に必ず残り、この穴が顕在化しなかった）。
    // attention エントリは stage 射影（deriveInterestStages）と再集計の interestStages 保存から
    // 除外される —「欠落 = attention」の読み替え契約は不変で、事実メタだけが保存される。
    const outKeys = new Set([
        ...Object.keys(stages),
        ...lastTouch.keys(),
        ...couponUntil.keys(),
        ...lastAction.keys(),
        ...cycleDue.keys(),
    ]);
    for (const key of outKeys) {
        const detail = { stage: stages[key] ?? 'attention' };
        const touch = lastTouch.get(key);
        if (touch)
            detail.lastTouchAt = touch.iso;
        const coupon = couponUntil.get(key);
        if (coupon)
            detail.couponUsableUntil = coupon.iso;
        const act = lastAction.get(key);
        if (act)
            detail.lastActionAt = act.iso;
        const due = cycleDue.get(key);
        if (due)
            detail.cycleDueAt = due.iso;
        out[key] = detail;
    }
    return out;
}
/**
 * 2 つのアンケート回答のうち `next` を「最新回答」として採用すべきか判定する
 * （M-73 仕上げ / **全順序** — 同一入力集合なら走査順・適用順に依らず勝者が一意）。
 *
 * answeredAt の新しい方 → 同時刻は `SURVEY_CHOICE_KEYS` のインデックスが小さい方
 * （`not_interested` が先頭 = 「同時刻は抑制側・安全側」の規約を内包）→ さらに同値なら
 * surveyId の辞書順（小さい方）。完全同値は false（既存維持 = 冪等）。
 * 増分（trackEvent の bumpLastSurveyAnswer）と全件再集計（deriveLastSurveyAnswers）が
 * 同じ勝者に収束するための唯一の比較真実源。
 */
export function surveyAnswerSupersedes(next, prev) {
    const nextMs = Date.parse(next.answeredAt);
    const prevMs = Date.parse(prev.answeredAt);
    if (!Number.isFinite(nextMs))
        return false;
    if (!Number.isFinite(prevMs))
        return true;
    if (nextMs !== prevMs)
        return nextMs > prevMs;
    const nextRank = SURVEY_CHOICE_KEYS.indexOf(next.answer);
    const prevRank = SURVEY_CHOICE_KEYS.indexOf(prev.answer);
    if (nextRank !== prevRank)
        return nextRank < prevRank;
    return (next.surveyId ?? '') < (prev.surveyId ?? '');
}
/**
 * promo_events から商材ごとの**最新アンケート回答**を導出する（M-73 仕上げ / 純関数・順序非依存）。
 *
 * `AppUser.lastSurveyAnswers` の全件再集計用（増分は trackEvent の bumpLastSurveyAnswer）。
 * - `evaluatedAt` より後・時刻不明のイベントは無視（他 derive と同じ基準時刻規約 / parse 不能は throw）
 * - 未知の回答値（SURVEY_CHOICE_KEYS 外）・商材未解決は無視（fail-closed）
 * - 勝者判定は `surveyAnswerSupersedes`（全順序 / 同時刻は not_interested 優先を内包）に一本化 —
 *   events の走査順（Firestore query は orderBy なし = doc id 順）に依らず結果が一意
 */
export function deriveLastSurveyAnswers(events, evaluatedAt, config) {
    const cutoff = Date.parse(evaluatedAt);
    if (!Number.isFinite(cutoff)) {
        throw new RangeError(`deriveLastSurveyAnswers: invalid evaluatedAt: ${evaluatedAt}`);
    }
    const best = new Map();
    for (const event of events) {
        if (event.eventType !== 'survey_answer')
            continue;
        const choice = event.payload?.surveyChoice;
        if (!choice || !SURVEY_CHOICE_KEYS.includes(choice))
            continue;
        const at = Date.parse(event.createdAt);
        if (!Number.isFinite(at) || at > cutoff)
            continue;
        const key = resolveInterestKeyFromEvent(event, config);
        if (!key)
            continue;
        const surveyId = event.payload?.surveyId;
        const entry = {
            answer: choice,
            answeredAt: event.createdAt,
            ...(surveyId ? { surveyId } : {}),
        };
        const prev = best.get(key);
        if (!prev || surveyAnswerSupersedes(entry, prev))
            best.set(key, entry);
    }
    const out = {};
    for (const [key, v] of best)
        out[key] = v;
    return out;
}
/**
 * `createdAt <= evaluatedAt` のイベントだけを返す（M-119 リプレイ基盤の薄いヘルパ）。
 *
 * 除外規約は `deriveInterestStageDetails` の 2 パス目（`!Number.isFinite(at) || at > cutoff`）
 * と同一 — parse 不能（createdAt 欠落 = `''`）も落とす。用途は**確度側**の事前フィルタ:
 * `deriveInterestTags` / `applyEventToInterest` は `createdAt` を受け取らず `now` を
 * `updatedAt` の刻印にしか使わないため、過去時刻 T で確度をリプレイするには呼出側で
 * T 以後のイベントを取り除く必要がある（`deriveInterestTags` 自体のシグネチャは変えない =
 * 増分経路 `applyEventToInterest` に影響させない）。
 *
 * `evaluatedAt` parse 不能は throw（基準時刻の黙殺 = fail-open を防ぐ / derive 群と同じ）。
 * 入力は変異させず新しい配列を返す。
 */
export function filterEventsUpTo(events, evaluatedAt) {
    const cutoff = Date.parse(evaluatedAt);
    if (!Number.isFinite(cutoff)) {
        throw new RangeError(`filterEventsUpTo: invalid evaluatedAt: ${evaluatedAt}`);
    }
    return events.filter((event) => {
        const at = Date.parse(event.createdAt);
        return Number.isFinite(at) && at <= cutoff;
    });
}
/**
 * 複数イベントから商材ごとの到達最高ステージをバッチ算出する（§4.3 ①〜③）。
 * `deriveInterestStageDetails` の stage 射影（判定仕様はそちらのコメントを参照）。
 * 減衰（§4.3 ④ / M-72 `applyStageDecay`）は本関数の後段に乗せる。順序非依存・非破壊。
 */
export function deriveInterestStages(events, evaluatedAt, config, rules = DEFAULT_STAGE_PROMOTION_RULES) {
    const details = deriveInterestStageDetails(events, evaluatedAt, config, rules);
    const out = {};
    for (const [key, detail] of Object.entries(details)) {
        // 事実アンカーのみのエントリ（stage='attention'）は射影に含めない（欠落 = attention）。
        if (detail.stage === 'attention')
            continue;
        out[key] = detail.stage;
    }
    return out;
}
/**
 * 単一商材のステージを算出する（§4.3 判定フロー / ユーザー×商材単位の評価入口）。
 * 後続の (ステージ×確度)→打ち手マトリクス（§6）はこの戻り値と確度タグを組み合わせる。
 */
export function deriveStageForKey(events, key, evaluatedAt, config, rules = DEFAULT_STAGE_PROMOTION_RULES) {
    return deriveInterestStages(events, evaluatedAt, config, rules)[key] ?? 'attention';
}
// ---- M-72: 評価時減衰（§4.2【確定 2026-07-14: 案A】/ §4.3 ④） ----
// 保存値（interestStages / InterestTag）は書き換えず、セグメント抽出・配信対象判定の
// 評価時に「最終接触からの経過 JST 暦日差」でステージを割り引く。確度は減衰しない。
/** クーポン取得起点の D 鮮度猶予（店頭利用期限＋14日 / §4.2）。 */
export const COUPON_DESIRE_GRACE_DAYS = 14;
/**
 * 周期再セット起点の D 鮮度猶予（期限＋14日 / M-75）。COUPON_DESIRE_GRACE_DAYS と同型 —
 * 再セット D は期限（`cycleDueAt`）+ 猶予まで減衰から保持し、未行動のまま期限超過なら
 * D→I へ自然減衰させる（空振り配信の抑制）。車検は lead 3 か月（≒92 日）> desire 減衰
 * 90 日のため、このアンカーが無いと再セット D が期限前に失効する。
 */
export const CYCLE_DESIRE_GRACE_DAYS = 14;
/**
 * ステージ減衰の既定値（§4.2: D 60日 / I 180日）。商材×テナント別の値は M-74 で
 * config 化済み — 評価側は promoEngine.ts の `resolveStageDecayRule(key, config)` で
 * 商材別に解決して `applyStageDecay` へ注入する（§5 タイプ②の「減衰せず追跡」= null は
 * そこで流れ込む）。本定数は `applyStageDecay` の後方互換デフォルト。
 */
export const DEFAULT_STAGE_DECAY_RULE = {
    desireDays: 60,
    interestDays: 180,
};
/**
 * 評価時減衰（§4.3 ④）: 保存ステージに経過日数を適用した実効ステージを返す（純関数）。
 * - 日差の判定は M-71 の鮮度窓と同じ両端含む規約（日差 = N 日までは窓内、N+1 で減衰）
 * - desire の鮮度根拠は「最終接触 + desireDays」「クーポン利用期限 + 14日」
 *   「周期期限（cycleDueAt）+ 14日（M-75）」の max
 *   （クーポン/周期期限は D の保持にのみ効く。I の鮮度は最終接触のみ）
 * - interest 鮮度切れは attention + dormant（休眠。タグ = 確度は保持したまま）
 * - action は減衰しない（事実のみで成立）。attention はそのまま
 * - 判定材料なし（meta 欠落 / lastTouchAt parse 不能）は fail-closed で減衰する
 *   （鮮度不明の古いスナップショットを強い打ち手の対象に残さない）
 * - `evaluatedAt` parse 不能は throw（基準時刻の黙殺 = fail-open を防ぐ）
 */
export function applyStageDecay(stage, meta, evaluatedAt, rule = DEFAULT_STAGE_DECAY_RULE) {
    const evalMs = Date.parse(evaluatedAt);
    if (!Number.isFinite(evalMs)) {
        throw new RangeError(`applyStageDecay: invalid evaluatedAt: ${evaluatedAt}`);
    }
    if (stage === 'action' || stage === 'attention') {
        return { stage, dormant: false };
    }
    const evalDay = jstDayNumber(evalMs);
    const touchMs = meta?.lastTouchAt !== undefined ? Date.parse(meta.lastTouchAt) : NaN;
    const touchDay = Number.isFinite(touchMs) ? jstDayNumber(touchMs) : null;
    /** 最終接触から days 日以内か（null は減衰しない設定 / 接触不明は false）。 */
    const touchWithin = (days) => days === null || (touchDay !== null && evalDay - touchDay <= days);
    let effective = stage;
    if (effective === 'desire') {
        const couponMs = meta?.couponUsableUntil !== undefined ? Date.parse(meta.couponUsableUntil) : NaN;
        const couponFresh = Number.isFinite(couponMs) &&
            evalDay <= jstDayNumber(couponMs) + COUPON_DESIRE_GRACE_DAYS;
        // M-75: 周期再セットの D は期限（cycleDueAt）+ 14日まで保持する。
        const cycleMs = meta?.cycleDueAt !== undefined ? Date.parse(meta.cycleDueAt) : NaN;
        const cycleFresh = Number.isFinite(cycleMs) &&
            evalDay <= jstDayNumber(cycleMs) + CYCLE_DESIRE_GRACE_DAYS;
        if (!touchWithin(rule.desireDays) && !couponFresh && !cycleFresh) {
            effective = 'interest';
        }
    }
    if (effective === 'interest' && !touchWithin(rule.interestDays)) {
        return { stage: 'attention', dormant: true };
    }
    return { stage: effective, dormant: false };
}
//# sourceMappingURL=promoInterest.js.map